import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { getActiveSubscriptionCookie } from '~/features/cookies/utils/get-active-subscription-cookie';
import { useUser } from '~/features/user/hooks/use-user';
import { extractCompanySlug } from '~/utils/extract-company-slug';

/**
 * Pure decision helper: returns the URL we should `router.replace` to in order
 * to send an enterprise learner from the consumer `/course/<slug>` page to
 * their `/org/<companySlug>/course/<slug>` variant. Returns `null` when no
 * redirect is needed (consumer user, prefix not yet resolved, or the user is
 * not actually on a consumer course path).
 *
 * Exported separately so it can be unit-tested without mocking `next/router`.
 */
export function getEnterprisePlpRedirectTarget({ asPath, prefix }: { asPath: string; prefix: string }): string | null {
  if (!prefix) return null;
  if (!asPath.startsWith('/course/')) return null;
  return `${prefix}${asPath}`;
}

/**
 * Client-side safety net: when an enterprise learner lands on a consumer
 * `/course/<slug>` page (e.g. via a stale link or the legacy enterprise PLP
 * server-redirecting an unauthorized request back to the consumer route),
 * replace history with their `/org/<companySlug>/course/<slug>` variant.
 *
 * NOTE: All cookie and query-param reads happen inside `useEffect` so the
 * render phase stays deterministic between SSR and the first client render —
 * `getActiveSubscriptionCookie()` reads `document.cookie` directly and would
 * otherwise produce a different value on the server vs. the client and trip
 * React's hydration warnings.
 */
export function useEnterprisePlpRedirect() {
  const router = useRouter();
  const { userCatalog } = useUser();

  useEffect(() => {
    const userHasAllAccess = getActiveSubscriptionCookie();
    const companySlugQueryParam = router.query.companySlug;
    const userCompanySlug = extractCompanySlug(userCatalog?.catalogUrl);

    let prefix = '';
    if (typeof companySlugQueryParam === 'string' && companySlugQueryParam.length > 0) {
      prefix = `/org/${companySlugQueryParam}`;
    } else if (userCompanySlug && !userHasAllAccess) {
      prefix = `/org/${userCompanySlug}`;
    }

    const target = getEnterprisePlpRedirectTarget({ asPath: router.asPath, prefix });
    if (!target) return;
    router.replace(target);
  }, [router, userCatalog]);
}
